import { lazy, Suspense, type CSSProperties } from "react";
import { Box, HtVideo, Spinner } from "@hightouch/htui";
import type { VideoComposition } from "@hightouch/ad-studio-renderer";
import { AdAsset } from "src/pages/ad-studio/components/ad-asset";
import { THUMBNAIL_RESIZE_WIDTH } from "src/pages/ad-studio/components/ad-grid-metrics";
import {
  type AdAssetPreviewAsset,
  getAdAssetKind,
} from "src/pages/ad-studio/components/ad-asset-kind";
import VideoAssetThumbnail from "src/pages/ad-studio/components/video-asset-thumbnail";

export type { AdAssetPreviewAsset } from "src/pages/ad-studio/components/ad-asset-kind";
export {
  type AdAssetKind,
  getAdAssetKind,
} from "src/pages/ad-studio/components/ad-asset-kind";

// Lazy so the full Remotion player only loads when a "full" preview is rendered.
// Thumbnails use VideoAssetThumbnail (a lighter Remotion Thumbnail) instead.
const CompositionPlayer = lazy(() =>
  import("src/pages/ad-studio/composition/composition-player").then((m) => ({
    default: m.CompositionPlayer,
  })),
);

const FALLBACK_VIDEO_ASPECT_RATIO = "16 / 9";

const FULL_MEDIA_STYLE: CSSProperties = {
  maxWidth: "100%",
  maxHeight: "100%",
  objectFit: "contain",
};

type AdAssetPreviewProps = {
  asset?: AdAssetPreviewAsset | null;
  /**
   * "thumbnail" — compact grid tile: composition first-frame with hover-to-play,
   * autoplaying muted video, cover-fit image.
   * "full" — interactive preview: composition player with controls, video with
   * controls, contain-fit image. The caller owns the surrounding container.
   */
  variant: "thumbnail" | "full";
  /** Set by a caller whose list virtualizes; see `AdAsset`'s prop. */
  isVirtualized?: boolean;
  /**
   * Lets a parent drive thumbnail composition playback when its own hover can't
   * fire (e.g. the card stacks a full-cover click target over this element).
   */
  isHovering?: boolean;
  /**
   * The default suits a grid tile; a small tile must scale it down or the badge
   * clips against the container's rounded corners.
   */
  playIconSize?: string;
  /** Loads a thumbnail's static ad image layers and motion stills through the resizer at this width. */
  thumbnailImageWidth?: number;
};

/**
 * Renders any ad asset — motion composition, raw video file, or static
 * image/HTML — in a compact ("thumbnail") or interactive ("full") form. Returns
 * `null` when there is nothing renderable, so callers own their own empty/error
 * placeholder. This is the single dispatch point for "asset → pixels"; keep the
 * type-branching here rather than duplicating it per surface.
 */
export function AdAssetPreview({
  asset,
  variant,
  isVirtualized,
  isHovering,
  playIconSize = "4xl",
  thumbnailImageWidth,
}: AdAssetPreviewProps) {
  const kind = getAdAssetKind(asset);
  const isThumbnail = variant === "thumbnail";

  if (kind === "empty" || !asset) {
    return null;
  }

  if (kind === "composition") {
    // Non-null: getAdAssetKind only returns "composition" when it is present.
    const composition = asset.video_composition as VideoComposition;
    if (isThumbnail) {
      const aspectRatio =
        composition.width > 0 && composition.height > 0
          ? `${composition.width} / ${composition.height}`
          : FALLBACK_VIDEO_ASPECT_RATIO;
      return (
        <Box
          width="100%"
          overflow="hidden"
          position="relative"
          style={{ aspectRatio }}
        >
          <VideoAssetThumbnail
            asset={asset}
            iconSize={playIconSize}
            isHovering={isHovering}
            imageWidth={thumbnailImageWidth}
          />
        </Box>
      );
    }
    return (
      <Suspense fallback={<Spinner size="lg" />}>
        <CompositionPlayer
          composition={composition}
          componentAssets={asset.component_assets}
          controls
          autoPlay
          loop
          initiallyMuted
          clickToPlay={false}
          style={{ width: "100%", height: "100%" }}
        />
      </Suspense>
    );
  }

  if (kind === "video-file" && asset.asset_url) {
    if (isThumbnail) {
      return (
        <Box width="100%" overflow="hidden">
          <HtVideo
            decorative
            playback="autoplay"
            src={asset.asset_url}
            width="100%"
            display="block"
            preload="auto"
          />
        </Box>
      );
    }
    return (
      <HtVideo alt="Ad asset" src={asset.asset_url} style={FULL_MEDIA_STYLE} />
    );
  }

  // kind === "static"
  return (
    <Box width="100%" overflow="hidden">
      <AdAsset
        src={asset.asset_url ?? ""}
        svg={asset.svg}
        componentAssets={asset.component_assets}
        componentAssetUrls={asset.component_asset_urls}
        alt="Ad asset"
        width="100%"
        naturalWidth={
          isThumbnail
            ? (thumbnailImageWidth ?? THUMBNAIL_RESIZE_WIDTH)
            : undefined
        }
        isVirtualized={isVirtualized}
        objectFit={isThumbnail ? "cover" : "contain"}
      />
    </Box>
  );
}
